// The season timelapse player: the frame, the play state and the speed
// (ui-map-interface.md §3.6, R1620).
//
// One player drives one place. Its position runs from 0 to `max`. In the
// summary card and the public reader, `max` is the last frame. In WAR ·
// SOVEREIGNTY it is one past the last frame, and that position is the live
// map ("now").
//
// The clock is the page's animation frame, and it runs only while the
// player plays: a paused player schedules nothing (§10.3). The frames are
// steps, so the player needs no tween, and reduced motion changes nothing
// here: nothing plays until the reader presses PLAY. A test steps the
// player with `advance` and never touches the animation frame.
//
// **The sting comes first** (R1655, brand.md §11). PLAY that starts the
// play at frame 0 plays the sting once before frame 0: for `introSec` the
// clock runs `intro` and the position stays at 0, and a view draws the
// sting over its map (`intro.ts`). A tap skips it (`skipIntro`). A seek, a
// pause and the end of the play never play it, and they stop one that
// plays.

import { TIMELAPSE_FPS, TIMELAPSE_MAX_STEP_MS, TIMELAPSE_SPEEDS } from '../../gen/presentation';
import { STING_SEC } from '../sting';

/** What schedules the next step: the animation frame in a browser. */
export interface PlayerClock {
  request(fn: (ms: number) => void): number;
  cancel(id: number): void;
}

/** The page's animation frame, or no clock outside a browser. */
function pageClock(): PlayerClock | null {
  if (typeof requestAnimationFrame !== 'function') return null;
  return {
    request: (fn) => requestAnimationFrame(fn),
    cancel: (id) => cancelAnimationFrame(id),
  };
}

export class TimelapsePlayer {
  /** The position: a frame index, or `max` for the end. */
  frame = 0;
  /** The last position. */
  max = 0;
  playing = false;
  speed: number;
  /**
   * The position the view opened on, or -1 for none. PLAY from it starts
   * at frame 0, as PLAY from the end does: the summary card and the reader
   * open on the sea that ended the season, and PLAY plays the season.
   */
  home = -1;
  /** The time of the sting before frame 0, in seconds, while it plays;
   *  -1 while it does not. */
  intro = -1;
  /** The share of the next frame the clock has already paid. */
  private acc = 0;
  private readonly subs = new Set<() => void>();
  private raf = 0;
  private lastMs = -1;

  constructor(
    private readonly fps = TIMELAPSE_FPS,
    private readonly speeds: readonly number[] = TIMELAPSE_SPEEDS,
    private readonly clock: PlayerClock | null = pageClock(),
    /** The length of the sting before frame 0; 0 plays none. */
    private readonly introSec = STING_SEC,
  ) {
    this.speed = speeds[0] ?? 1;
  }

  /** Calls `fn` after every change of the position, the play state or
   *  the speed. Answers the call that stops it. */
  on(fn: () => void): () => void {
    this.subs.add(fn);
    return () => { this.subs.delete(fn); };
  }

  get atEnd(): boolean { return this.frame >= this.max; }

  /**
   * Takes a new last position. `follow` keeps a player that stood at the
   * end at the new end: the live map of WAR stays live when a frame lands.
   * Any other position stays, inside the new bounds.
   */
  setMax(max: number, follow: boolean): void {
    const m = Math.max(0, Math.floor(max));
    const wasEnd = this.atEnd;
    this.max = m;
    if (follow && wasEnd) this.frame = m;
    else this.frame = Math.min(this.frame, m);
    if (this.frame >= m && this.playing) this.stop();
    this.emit();
  }

  /** Moves to one position and pauses: a drag of the scrubber, a press of
   *  LIVE, ▸ ON THE MAP. */
  seek(frame: number): void {
    const f = Math.min(this.max, Math.max(0, Math.floor(frame)));
    const moved = f !== this.frame || this.playing;
    this.frame = f;
    this.stop();
    if (moved) this.emit();
  }

  /** PLAY or PAUSE. A play from the end, or from the home position,
   *  starts at frame 0. */
  toggle(): void {
    if (this.playing) {
      this.stop();
    } else {
      if (this.max === 0) return;
      if (this.atEnd || this.frame === this.home) this.frame = 0;
      this.playing = true;
      this.acc = 0;
      // A play from day 0 opens with the sting (R1655).
      this.intro = this.frame === 0 && this.introSec > 0 ? 0 : -1;
      this.schedule();
    }
    this.emit();
  }

  /** A tap on the sting: the play goes on from frame 0 at once. */
  skipIntro(): void {
    if (this.intro < 0) return;
    this.intro = -1;
    this.acc = 0;
    this.emit();
  }

  /** Stops the play and keeps the position. */
  pause(): void {
    if (!this.playing) return;
    this.stop();
    this.emit();
  }

  /** The speed button: the next speed of the list, then the first again. */
  cycleSpeed(): void {
    const i = this.speeds.indexOf(this.speed);
    this.speed = this.speeds[(i + 1) % this.speeds.length] ?? 1;
    this.emit();
  }

  /**
   * Pays `dtSec` of the play clock. The position moves one frame for each
   * 1 / (fps × speed) seconds, and the play stops at the end. Answers true
   * when the position moved.
   */
  advance(dtSec: number): boolean {
    if (!this.playing) return false;
    if (this.intro >= 0) {
      // The sting runs on the play clock, at every speed, and frame 0
      // waits under it. The rest of the step is not carried into frame 0.
      this.intro += Math.max(0, dtSec);
      if (this.intro >= this.introSec) this.intro = -1;
      this.emit();
      return false;
    }
    this.acc += Math.max(0, dtSec) * this.fps * this.speed;
    let moved = false;
    while (this.acc >= 1 && this.playing) {
      this.acc -= 1;
      if (this.frame < this.max) {
        this.frame++;
        moved = true;
      }
      if (this.frame >= this.max) this.stop();
    }
    if (moved || !this.playing) this.emit();
    return moved;
  }

  /** Stops the clock for good: the view that owns the player has gone. */
  dispose(): void {
    this.stop();
    this.subs.clear();
  }

  private stop(): void {
    this.playing = false;
    this.intro = -1;
    this.acc = 0;
    if (this.raf !== 0 && this.clock) this.clock.cancel(this.raf);
    this.raf = 0;
    this.lastMs = -1;
  }

  private schedule(): void {
    if (!this.clock || this.raf !== 0 || !this.playing) return;
    this.raf = this.clock.request(this.onFrame);
  }

  private readonly onFrame = (ms: number): void => {
    this.raf = 0;
    if (!this.playing) return;
    const dt = this.lastMs < 0 ? 0 : Math.min(ms - this.lastMs, TIMELAPSE_MAX_STEP_MS) / 1000;
    this.lastMs = ms;
    this.advance(dt);
    this.schedule();
  };

  private emit(): void {
    for (const fn of this.subs) fn();
  }
}
